import React, { useEffect, useCallback } from 'react';
import { debug } from '@/utils/debug';
import { formatAccountId } from '@/utils/googleAds';
import styles from './PollingStates.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface PollingSuccessProps {
  customerId: string;
  result: { campaigns_found?: number; total_budget?: string; account_name?: string } | null;
  onGoHome: () => void;
}

export function PollingSuccess({ customerId, result, onGoHome }: PollingSuccessProps) {
  debug.log('PollingSuccess: Rendering success state', { customerId, result });

  const handleGoHome = () => {
    debug.log('PollingSuccess: Go home requested');
    onGoHome();
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onGoHome();
    }
  }, [onGoHome]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const modalTitleId = 'polling-success-title';

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>Account Connected Successfully!</h3>
          <button
            className={modalStyles.modalClose}
            onClick={onGoHome}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={`${styles.icon} ${styles.successIcon}`}>✅</div>
            </div>

            <div className={styles.content}>
        <div className={styles.message}>
          <p className={styles.primaryText}>
            Your Google Ads account has been successfully linked.
          </p>
          <p className={styles.secondaryText}>
            <strong>Customer ID:</strong> {formatAccountId(customerId)}
          </p>
        </div>

        <div className={`${styles.infoSection} ${styles.successSection}`}>
          <h4 className={styles.sectionTitle}>What happens next?</h4>
          <ul className={styles.stepsList}>
            <li>Your campaigns and budget data will be synchronized.</li>
            <li>You can now set up budget and bidding optimization strategies.</li>
            <li>Flouri will begin monitoring your account performance, and make adjustments automatically following your optimization settings.</li>
            <li>You'll receive notifications about optimization opportunities. You can update your notification preferences in your profile settings.</li>
          </ul>
        </div>

        {result && result.campaigns_found && (
          <div className={`${styles.infoSection} ${styles.dataSection}`}>
            <h4 className={styles.sectionTitle}>Account Summary</h4>
            <div className={styles.dataGrid}>
              <div className={styles.dataStat}>
                <span className={styles.dataValue}>{result.campaigns_found}</span>
                <span className={styles.dataLabel}>Campaigns Found</span>
              </div>
              {result.total_budget && (
                <div className={styles.dataStat}>
                  <span className={styles.dataValue}>${result.total_budget}</span>
                  <span className={styles.dataLabel}>Total Daily Budget</span>
                </div>
              )}
              {result.account_name && (
                <div className={styles.dataStat}>
                  <span className={styles.dataValue}>{result.account_name}</span>
                  <span className={styles.dataLabel}>Account Name</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleGoHome}
          >
            Continue to Dashboard
          </button>
        </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}